import React from 'react';
import { Link } from 'react-router-dom';
import { Shield } from 'lucide-react';

const LAST_UPDATED = 'November 4, 2026';

const Section = ({ title, children }) => (
  <section className="mb-10">
    <h2 className="text-2xl font-bold text-gray-900 mb-4">{title}</h2>
    <div className="space-y-4 text-gray-600 leading-relaxed">{children}</div>
  </section>
);

export const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen pt-24 pb-16">
      {/* Hero */}
      <section className="py-16 px-4 bg-gradient-to-br from-orange-50 via-blue-50 to-white">
        <div className="container mx-auto max-w-4xl text-center">
          <div className="w-16 h-16 bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl flex items-center justify-center mx-auto mb-6">
            <Shield className="h-8 w-8 text-white" />
          </div>
          <h1 className="text-4xl lg:text-5xl font-bold mb-4">
            Privacy <span className="text-orange-500">Policy</span>
          </h1>
          <p className="text-gray-600">Last updated: {LAST_UPDATED}</p>
        </div>
      </section>

      {/* Content */}
      <section className="py-16 px-4 bg-white">
        <div className="container mx-auto max-w-4xl">
          <Section title="1. Introduction">
            <p>
              SolarBull Energy LLP ("SolarBull", "we", "us" or "our") respects your privacy. This
              Privacy Policy explains how we collect, use, share and protect personal information when
              you visit www.solarbull.in (the "Website"), use our solar savings calculator, chat with
              us, or contact us about our services.
            </p>
            <p>
              By using the Website, you agree to the practices described in this policy. This policy is
              published in accordance with the Information Technology Act, 2000, the rules made under
              it, and the Digital Personal Data Protection Act, 2023, as applicable.
            </p>
          </Section>

          <Section title="2. Information We Collect">
            <p><strong className="text-gray-900">Information you provide to us:</strong></p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Contact details such as your name, email address and phone number.</li>
              <li>Property type, electricity usage, location and other details you enter into our
                contact form or solar calculator.</li>
              <li>Messages you send through our contact form, chat support, WhatsApp, email or phone.</li>
            </ul>
            <p><strong className="text-gray-900">Information collected automatically:</strong></p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Device and browser information, IP address, pages visited, referring URLs and the
                date and time of your visit.</li>
              <li>Information collected through cookies, pixels and similar technologies (see
                Section 4).</li>
            </ul>
          </Section>

          <Section title="3. How We Use Your Information">
            <ul className="list-disc pl-6 space-y-2">
              <li>To respond to your enquiries and provide quotes, site assessments and proposals.</li>
              <li>To deliver, install, maintain and support our solar products and services.</li>
              <li>To help you with government schemes such as PM SURYA GHAR and PM-KUSUM, where
                you request it.</li>
              <li>To send you service updates and, where permitted, information about our offers.</li>
              <li>To measure the performance of our Website and advertising, and to improve our
                services.</li>
              <li>To comply with legal obligations and protect our rights.</li>
            </ul>
          </Section>

          <Section title="4. Cookies, Meta Pixel and Analytics">
            <p>
              We use cookies and similar technologies to operate the Website and understand how
              visitors use it. In particular, we use the <strong className="text-gray-900">Meta Pixel</strong>,
              a tool provided by Meta Platforms, Inc. (Facebook and Instagram). The Meta Pixel collects
              information such as your IP address, browser information, the pages you visit and actions
              you take on our Website. This helps us measure the effectiveness of our advertising, show
              relevant ads to people who have visited our Website, and build audiences for future ads.
            </p>
            <p>
              Meta may combine this information with other information it holds about you and
              processes it under its own{' '}
              <a href="https://www.facebook.com/privacy/policy/" target="_blank" rel="noopener noreferrer" className="text-orange-600 hover:text-orange-700 underline">
                Privacy Policy
              </a>. You can control how Meta uses this data for ads through your{' '}
              <a href="https://www.facebook.com/adpreferences/" target="_blank" rel="noopener noreferrer" className="text-orange-600 hover:text-orange-700 underline">
                Facebook ad preferences
              </a>.
            </p>
            <p>
              You can block or delete cookies through your browser settings. Doing so may affect how
              some parts of the Website work.
            </p>
          </Section>

          <Section title="5. How We Share Your Information">
            <p>We do not sell your personal information. We may share it with:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Service providers who help us run our business, such as hosting, communication,
                analytics and advertising providers (including Meta).</li>
              <li>Installation partners, equipment manufacturers, financing partners and DISCOMs,
                where needed to deliver the service you request.</li>
              <li>Government authorities or regulators, where required for subsidy applications or
                by law.</li>
              <li>A successor entity in the event of a merger, acquisition or sale of assets.</li>
            </ul>
          </Section>

          <Section title="6. Data Retention">
            <p>
              We keep your personal information only for as long as necessary to fulfil the purposes
              described in this policy, including providing ongoing O&amp;M and warranty support, or as
              required by law. When it is no longer needed, we delete or anonymise it.
            </p>
          </Section>

          <Section title="7. Data Security">
            <p>
              We use reasonable technical and organisational measures to protect your personal
              information against unauthorised access, loss or misuse. However, no method of
              transmission over the internet is completely secure, and we cannot guarantee absolute
              security.
            </p>
          </Section>

          <Section title="8. Your Rights">
            <p>Subject to applicable law, you may:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Request access to the personal information we hold about you.</li>
              <li>Ask us to correct or update inaccurate information.</li>
              <li>Ask us to delete your information, or withdraw consent you have given.</li>
              <li>Opt out of marketing messages at any time.</li>
              <li>Raise a grievance with us about how your data is handled.</li>
            </ul>
            <p>To exercise these rights, contact us using the details below.</p>
          </Section>

          <Section title="9. Third-Party Links">
            <p>
              Our Website may link to third-party websites such as Facebook, Instagram, LinkedIn, X and
              WhatsApp. We are not responsible for the privacy practices of those websites, and we
              encourage you to read their privacy policies.
            </p>
          </Section>

          <Section title="10. Children's Privacy">
            <p>
              Our Website and services are not directed at children under 18, and we do not knowingly
              collect personal information from them.
            </p>
          </Section>

          <Section title="11. Changes to This Policy">
            <p>
              We may update this Privacy Policy from time to time. Changes take effect when posted on
              this page, and the "Last updated" date above will be revised accordingly.
            </p>
          </Section>

          <Section title="12. Contact Us / Grievance Officer">
            <p>
              If you have any questions, requests or complaints about this Privacy Policy or your
              personal information, please contact us:
            </p>
            <div className="bg-orange-50 rounded-xl p-6 text-gray-700">
              <p className="font-semibold text-gray-900">SolarBull Energy LLP</p>
              <p>
                8-1-301, Flat T3, Sa Re Ga Ma Apts, Above Axis Bank, 302, Shaikpet Rd, next to Karachi
                Bakery, Sabza Colony, Ambedkar Nagar, Toli Chowki, Hyderabad, Telangana 500104
              </p>
              <p className="mt-2">
                Email:{' '}
                <a href="mailto:srini@solarbull.in" className="text-orange-600 hover:text-orange-700 underline">
                  srini@solarbull.in
                </a>
              </p>
              <p>
                Phone:{' '}
                <a href="tel:+919553746982" className="text-orange-600 hover:text-orange-700 underline">
                  +91 9553746982
                </a>
              </p>
            </div>
            <p>
              You can also reach us through our <Link to="/contact" className="text-orange-600 hover:text-orange-700 underline">Contact page</Link>.
            </p>
          </Section>
        </div>
      </section>
    </div>
  );
};
